import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { promptFromPayload } from "~/api/ideTypes";
import { useDispatches, useFlows, useRunFlow } from "~/api/queries";
import type { Dispatch } from "~/api/types";
import { Pill } from "~/features/automation/components";
import { dispatchStatusPresentation } from "~/features/console/dispatchStatus";
import { Button } from "~/foundation/components/Button";
import { formatRelativeTime } from "~/foundation/utils/time";

/** kms clips the prompt at 120 and the result at 400; a rail is scanned, not read. */
const PROMPT_CLIP = 120;
const RESULT_CLIP = 400;

function clip(text: string, limit: number): string {
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
}

/**
 * What the machine can do, and what it has just done.
 *
 * Two of kms's four rails are deliberately not here (§E-11). The *Agent flows* rail read
 * TomatoFlow's `/flow-definitions` through a client W3 recorded as not-to-be-ported, and
 * *Changed this session* diffed against an in-memory baseline captured at page open — a
 * clever idea whose whole value ("the IDE just authored a flow while you watched") the flows
 * section shows better and keeps after a reload.
 */
export function MachineRail({
  targetSessionKey,
  dispatches,
}: {
  targetSessionKey: string;
  dispatches: Dispatch[];
}) {
  const { t, i18n } = useTranslation();
  const { data: flows } = useFlows();
  const run = useRunFlow();
  useDispatches();

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-1.5">
        <h3 className="text-body font-medium text-text">{t("Machine flows")}</h3>
        {(flows?.flows ?? []).length === 0 ? (
          <p className="text-small text-text-secondary">{t("No flows yet.")}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(flows?.flows ?? []).map((flow) => (
              <li key={flow.slug} className="flex items-center gap-2">
                <Link
                  to="/flows/$slug/edit"
                  params={{ slug: flow.slug }}
                  className="min-w-0 flex-1 truncate text-small text-text hover:underline"
                >
                  {flow.name}
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  // An archived flow has no runnable version; the server would refuse, so the
                  // button says so first.
                  disabled={flow.status === "archived" || run.isPending}
                  onClick={() =>
                    run.mutate(
                      {
                        slug: flow.slug,
                        request: {
                          host: "ide",
                          // W3 already carries this field (§E-13) — the rail's whole point is
                          // running the flow in the window you are looking at.
                          ...(targetSessionKey ? { targetSessionKey } : {}),
                        },
                      },
                      {
                        onSuccess: () => toast.success(t("Flow dispatched to your IDE")),
                        onError: (error) =>
                          toast.error(
                            error instanceof Error ? error.message : t("Could not run the flow"),
                          ),
                      },
                    )
                  }
                >
                  {t("Run")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-1.5">
        <h3 className="text-body font-medium text-text">{t("Recent dispatches")}</h3>
        {dispatches.length === 0 ? (
          <p className="text-small text-text-secondary">{t("Nothing dispatched yet.")}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {dispatches.slice(0, 8).map((dispatch) => {
              const presentation = dispatchStatusPresentation(dispatch.statusDetail, t, {
                queuedBehind: dispatch.queuedBehind,
                ownerUserName: dispatch.ownerUserName,
              });
              const prompt = promptFromPayload(dispatch.payload);
              return (
                <li key={dispatch.id} className="flex flex-col gap-0.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Pill>{dispatch.kind}</Pill>
                    <Pill tone={presentation.tone}>{presentation.label}</Pill>
                    <span className="text-small text-text-tertiary">
                      {formatRelativeTime(dispatch.createdAt, i18n.language)}
                    </span>
                  </div>
                  {presentation.note ? (
                    <span className="text-small text-text-tertiary">{presentation.note}</span>
                  ) : null}
                  {dispatch.redacted ? (
                    <span className="text-small text-text-tertiary">
                      {t("Someone else’s work")}
                    </span>
                  ) : prompt ? (
                    <span className="text-small text-text-secondary">
                      {clip(prompt, PROMPT_CLIP)}
                    </span>
                  ) : null}
                  {dispatch.error ? (
                    <span className="text-small text-danger">
                      {clip(dispatch.error, RESULT_CLIP)}
                    </span>
                  ) : dispatch.result ? (
                    <span className="text-small text-text-secondary">
                      {clip(dispatch.result, RESULT_CLIP)}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
